import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment/moment';
import { v1 as uuidv1 } from 'uuid';

import { log } from '../../../services/logging';
import { saveNewScene } from '../../../services/scene';
import type { Scene } from '../../../types/scene';
import type { Style } from '../../../types/style';
import type { Nullable } from '../../../types/utility';
import { isPerspectiveSharingAvailable } from '../../connections/connectionsDuck';
import { getPerspective } from '../../perspectives/perspectives.selector';
import { isSharedStorageAvailable, sharedStorageApi } from '../../shared-storage/shared-storage.api';
import { STYLE_TYPE_SCENE } from '../../styles/styles.const';
import type { RootState } from '../../types';
import { getLatestSceneVersion } from '../migrations';
import type { RequestId } from '../scene.types';
import { NAME } from './const';

export const buildScene = (
  userId?: Scene['createdBy'],
  perspectiveStyle?: Style,
  scenesLength?: number,
  canSaveScene = true,
): Nullable<Scene> => {
  const version = getLatestSceneVersion();
  if (!version || !userId) return null;

  let name = '';
  if (canSaveScene) {
    name = `Untitled Scene${scenesLength && scenesLength > 0 ? ` ${scenesLength + 1}` : ''}`;
  } else {
    name = moment().format();
  }

  const id = uuidv1();
  return {
    name,
    createdAt: new Date(),
    id,
    createdBy: userId,
    numOfNodes: 0,
    numOfRels: 0,
    roles: [],
    ranges: [],
    version,
    temporaryData: canSaveScene ? undefined : {},
    style: perspectiveStyle
      ? {
          ...perspectiveStyle,
          id,
          type: STYLE_TYPE_SCENE,
        }
      : undefined,
  };
};

type StoreSceneResult = {
  scene: Scene;
  requestId: RequestId;
};

export const storeSceneThunk = createAsyncThunk<
  StoreSceneResult,
  Scene,
  {
    state: RootState;
    rejectValue: { requestId: RequestId };
  }
>(`${NAME}/storeSceneThunk`, async (sceneToStore, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const perspective = getPerspective(state);
  const isPluginPresent = isPerspectiveSharingAvailable(state);
  const requestId = sceneToStore.id;

  if (!perspective) {
    rejectWithValue({ requestId });
    throw Error('Perspective not found');
  }

  try {
    if (isPluginPresent) {
      await saveNewScene(perspective.id, sceneToStore);
    } else if (isSharedStorageAvailable()) {
      const result = await dispatch(sharedStorageApi.endpoints.createScene.initiate(sceneToStore));
      const { id, lastModified, createdAt, style } = result.data ?? {};

      if (typeof id !== 'string' || !(lastModified instanceof Date) || !(createdAt instanceof Date)) {
        throw new Error('Could not create scene in Shared Storage');
      }

      sceneToStore = {
        ...sceneToStore,
        id,
        lastModified,
        createdAt,
        style,
      };
    }
  } catch (error) {
    log.error('Failed to create scene:', error);
    rejectWithValue({ requestId });
  }

  return {
    scene: sceneToStore,
    requestId,
  };
});
